// @ts-nocheck -- copied verbatim from the application being replaced.
//
// Quarantined, not exempted. This file has not been rewritten to this project's
// settings and does not pass them; checking it would report errors nobody is
// going to fix in place, and loosening the settings to accommodate it would
// apply that to code written here too. Vite still compiles it -- esbuild strips
// types without checking them.
//
// Delete this line when the file is rewritten. It is the marker for what is left
// to do, and the count of files carrying it is the size of the refactor.
"use client";

import * as React from "react";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import { cn } from "./utils";

type TooltipVariant = "default" | "success" | "warning" | "error" | "info";

// Enhanced Tooltip Provider with better defaults
function EnhancedTooltipProvider({
  delayDuration = 300,
  skipDelayDuration = 100,
  children,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Provider>) {
  return (
    <TooltipPrimitive.Provider
      delayDuration={delayDuration}
      skipDelayDuration={skipDelayDuration}
      {...props}
    >
      {children}
    </TooltipPrimitive.Provider>
  );
}

// Enhanced Tooltip Root
function EnhancedTooltip({
  children,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Root>) {
  return (
    <TooltipPrimitive.Root {...props}>
      {children}
    </TooltipPrimitive.Root>
  );
}

// Enhanced Tooltip Trigger
function EnhancedTooltipTrigger({
  className,
  children,
  asChild = true,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Trigger>) {
  return (
    <TooltipPrimitive.Trigger
      asChild={asChild}
      className={cn("outline-none", className)}
      {...props}
    >
      {children}
    </TooltipPrimitive.Trigger>
  );
}

// Enhanced Tooltip Content with better styling
function EnhancedTooltipContent({
  className,
  sideOffset = 6,
  children,
  variant = "default",
  size = "default",
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Content> & {
  variant?: TooltipVariant;
  size?: "sm" | "default" | "lg";
}) {
  const variantStyles = {
    default: "bg-gray-900 text-white border-gray-700",
    success: "bg-green-600 text-white border-green-500",
    warning: "bg-yellow-600 text-white border-yellow-500", 
    error: "bg-red-600 text-white border-red-500",
    info: "bg-blue-600 text-white border-blue-500"
  };

  const sizeStyles = {
    sm: "px-2 py-1 text-xs max-w-48",
    default: "px-3 py-1.5 text-sm max-w-64",
    lg: "px-4 py-2 text-base max-w-80"
  };

  return (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Content
        sideOffset={sideOffset}
        className={cn(
          // Base styles
          "z-50 overflow-hidden rounded-lg border shadow-lg",
          "animate-in fade-in-0 zoom-in-95",
          "data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95",
          "data-[side=bottom]:slide-in-from-top-2",
          "data-[side=left]:slide-in-from-right-2", 
          "data-[side=right]:slide-in-from-left-2",
          "data-[side=top]:slide-in-from-bottom-2",
          // Variant styles
          variantStyles[variant],
          // Size styles
          sizeStyles[size],
          className
        )}
        {...props}
      >
        {children}
        <TooltipPrimitive.Arrow 
          className={cn(
            "fill-current",
            variantStyles[variant].split(' ')[0].replace('bg-', 'text-')
          )} 
        />
      </TooltipPrimitive.Content>
    </TooltipPrimitive.Portal>
  );
}

// Quick Tooltip Component for simple use cases
interface QuickTooltipProps {
 readonly content: React.ReactNode;
 readonly children: React.ReactNode;
 readonly variant?: TooltipVariant;
 readonly size?: "sm" | "default" | "lg";
 readonly side?: "top" | "right" | "bottom" | "left";
 readonly align?: "start" | "center" | "end";
 readonly disabled?: boolean;
 readonly className?: string;
 readonly contentClassName?: string;
}

function QuickTooltip({
  content,
  children,
  variant = "default",
  size = "default", 
  side = "top",
  align = "center",
  disabled = false,
  className,
  contentClassName,
}: QuickTooltipProps) {
  if (disabled || !content) {
    return <>{children}</>;
  }

  return (
    <EnhancedTooltip>
      <EnhancedTooltipTrigger asChild className={className}>
        {children}
      </EnhancedTooltipTrigger>
      <EnhancedTooltipContent
        variant={variant}
        size={size}
        side={side}
        align={align}
        className={contentClassName}
      >
        {content}
      </EnhancedTooltipContent>
    </EnhancedTooltip>
  );
}

// Status Badge Tooltip Component
interface StatusTooltipProps {
 readonly status: "verified" | "claimed" | "claim-now";
 readonly children: React.ReactNode;
 readonly brandName?: string;
 readonly className?: string;
}

function StatusTooltip({ status, children, brandName, className }: StatusTooltipProps) {
  const getStatusContent = () => {
    switch (status) {
      case 'verified':
        return (
          <div className="space-y-1">
            <div className="font-medium">✓ Verified Brand</div>
            <div className="text-xs opacity-90">
              {brandName ? `${brandName} has been verified by Xtremand` : "This brand has been verified by Xtremand"}
            </div>
          </div>
        );
      case 'claimed':
        return (
          <div className="space-y-1">
            <div className="font-medium">🏢 Claimed Brand</div>
            <div className="text-xs opacity-90">
              {brandName ? `${brandName} has claimed their profile` : "This brand has claimed their profile"}
            </div>
          </div>
        );
      case 'claim-now':
        return (
          <div className="space-y-1">
            <div className="font-medium">📢 Available to Claim</div>
            <div className="text-xs opacity-90">
              Click to claim this brand profile
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  const getVariant = () => {
    switch (status) {
      case 'verified':
        return 'success' as const;
      case 'claimed':
        return 'default' as const; // Changed from 'info' to 'default' for grey styling
      case 'claim-now':
        return 'warning' as const;
      default:
        return 'default' as const;
    }
  };

  return (
    <EnhancedTooltip>
      <EnhancedTooltipTrigger asChild className={className}>
        {children}
      </EnhancedTooltipTrigger>
      <EnhancedTooltipContent
        variant={getVariant()}
        size="default"
        side="top"
      >
        {getStatusContent()}
      </EnhancedTooltipContent>
    </EnhancedTooltip>
  );
}

// Info Tooltip Component for various UI elements
interface InfoTooltipProps {
 readonly title?: string;
 readonly description: string;
 readonly children: React.ReactNode;
 readonly variant?: TooltipVariant;
 readonly icon?: React.ReactNode;
 readonly className?: string;
}

function InfoTooltip({ 
  title, 
  description, 
  children, 
  variant = "default",
  icon,
  className 
}: InfoTooltipProps) {
  return (
    <EnhancedTooltip>
      <EnhancedTooltipTrigger asChild className={className}>
        {children}
      </EnhancedTooltipTrigger>
      <EnhancedTooltipContent
        variant={variant}
        size="default"
        side="top"
      >
        <div className="space-y-1">
          {title && (
            <div className="font-medium flex items-center gap-1">
              {icon}
              {title}
            </div>
          )}
          <div className="text-xs opacity-90">{description}</div>
        </div>
      </EnhancedTooltipContent>
    </EnhancedTooltip>
  );
}

export {
  EnhancedTooltipProvider,
  EnhancedTooltip,
  EnhancedTooltipTrigger,
  EnhancedTooltipContent,
  QuickTooltip,
  StatusTooltip,
  InfoTooltip
};